import { useMemo, useState } from "react";

import { MetricBlock } from "../api";
import PlayerCell from "../components/PlayerCell";
import ProgressBar from "../components/ProgressBar";
import { classFor } from "../data/classes";

interface Boss {
  fightId: number;
  name?: string;
  kill: boolean;
  durationMs: number;
  /** Time WCL used for its own DPS denominator (can differ from durationMs on multi-phase fights). */
  totalTimeMs: number;
}

interface ByBossEntry {
  damage: number;
  perSec: number;
}

interface SourceRow {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  byBoss: Record<string, ByBossEntry>;
}

interface TableRow {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  perSec: number;
}

const fmt = (n: number) => n.toLocaleString("en-US");

/**
 * Tabbed boss-damage view. Instead of stacking one table per boss
 * (which gets to 250 rows on a 25-player / 10-boss raid), we render a
 * single damage table whose contents come from whichever boss tab the
 * user has selected. "Total" is the default and shows the same combined-
 * across-fights aggregate the old layout had.
 *
 * The familiar single-table pattern keeps casual users oriented while
 * still giving the depth: every boss is one click away.
 */
export default function BossDamage({ block }: { block: MetricBlock }) {
  const sourceRows = (block.rows as unknown as SourceRow[]) ?? [];
  const bosses = (block.bosses as unknown as Boss[]) ?? [];

  const wipeCount = bosses.filter((b) => !b.kill).length;
  const [showWipes, setShowWipes] = useState(false);
  const [selected, setSelected] = useState<"total" | number>("total");

  const visibleBosses = useMemo(
    () => bosses.filter((b) => b.kill || showWipes),
    [bosses, showWipes],
  );
  const visibleIds = useMemo(
    () => new Set(visibleBosses.map((b) => String(b.fightId))),
    [visibleBosses],
  );

  // If the user had a wipe selected and then turned wipes off, snap back
  // to Total so the page doesn't end up rendering an empty table.
  if (
    typeof selected === "number" &&
    !visibleIds.has(String(selected)) &&
    selected !== ("total" as unknown as number)
  ) {
    setSelected("total");
  }

  const totalTimeSec = useMemo(
    () => visibleBosses.reduce((a, b) => a + (b.totalTimeMs ?? 0), 0) / 1000,
    [visibleBosses],
  );

  const rows = useMemo<TableRow[]>(() => {
    if (selected === "total") {
      return sourceRows
        .map((r) => {
          let sum = 0;
          for (const [fid, entry] of Object.entries(r.byBoss)) {
            if (visibleIds.has(fid)) sum += entry.damage;
          }
          return {
            name: r.name,
            type: r.type,
            icon: r.icon,
            total: sum,
            perSec: totalTimeSec ? sum / totalTimeSec : 0,
          };
        })
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total);
    }
    const fid = String(selected);
    return sourceRows
      .map((r) => {
        const entry = r.byBoss[fid];
        return {
          name: r.name,
          type: r.type,
          icon: r.icon,
          total: entry?.damage ?? 0,
          // Match WCL's per-fight DPS by reading it straight from
          // total_damage_done — same number their UI shows.
          perSec: entry?.perSec ?? 0,
        };
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [sourceRows, selected, visibleIds, totalTimeSec]);

  if (!sourceRows.length || !visibleBosses.length) {
    return (
      <p className="text-sm text-gray-500">
        No boss damage yet. (Re-import the report so per-fight metrics get
        recomputed for this slug.)
      </p>
    );
  }

  // Header subtitle / metadata for the current selection.
  let title: string;
  let subtitle: string;
  let badge: React.ReactNode = null;
  if (selected === "total") {
    title = "Total Boss Damage";
    subtitle = `Across ${visibleBosses.length} ${
      showWipes ? "fights" : "kills"
    }`;
  } else {
    const b = bosses.find((bb) => bb.fightId === selected);
    title = b?.name ?? `Fight ${selected}`;
    subtitle = `${formatDuration(b?.durationMs)} · ${b?.kill ? "Kill" : "Wipe"}`;
    badge = b ? <KillBadge kill={b.kill} /> : null;
  }

  return (
    <div className="space-y-3">
      {wipeCount > 0 && (
        <div className="flex justify-end">
          <label className="flex items-center gap-2 text-xs text-gray-400 select-none">
            <input
              type="checkbox"
              checked={showWipes}
              onChange={(e) => setShowWipes(e.target.checked)}
              className="accent-accent"
            />
            Show wipes ({wipeCount})
          </label>
        </div>
      )}

      <BossTabs
        bosses={visibleBosses}
        selected={selected}
        onSelect={setSelected}
      />

      <DamageTable
        title={title}
        subtitle={subtitle}
        badge={badge}
        rows={rows}
      />
    </div>
  );
}

/* ----------------------------- Tabs ----------------------------- */

function BossTabs({
  bosses,
  selected,
  onSelect,
}: {
  bosses: Boss[];
  selected: "total" | number;
  onSelect: (s: "total" | number) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tab
        active={selected === "total"}
        onClick={() => onSelect("total")}
        label="Total"
      />
      {bosses.map((b) => (
        <Tab
          key={b.fightId}
          active={selected === b.fightId}
          onClick={() => onSelect(b.fightId)}
          label={b.name ?? `Fight ${b.fightId}`}
          dot={b.kill ? "kill" : "wipe"}
          aside={formatDuration(b.durationMs)}
        />
      ))}
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
  dot,
  aside,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  dot?: "kill" | "wipe";
  aside?: string;
}) {
  // Active tab gets the accent treatment; the rest stay subtle so 11 of
  // them in a row don't fight for attention. The dot is a single-pixel
  // colour cue (green=kill, red=wipe) that sits next to the boss name.
  return (
    <button
      onClick={onClick}
      title={aside ? `${label} · ${aside}` : label}
      className={[
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
        "transition-colors max-w-[14rem]",
        active
          ? "border-accent/40 bg-accent/15 text-accent"
          : "border-border/60 bg-bg/40 text-gray-300 hover:bg-white/5 hover:text-gray-100",
      ].join(" ")}
    >
      {dot && (
        <span
          className={
            "inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 " +
            (dot === "kill" ? "bg-emerald-400" : "bg-red-400")
          }
        />
      )}
      <span className="truncate">{label}</span>
      {aside && (
        <span className="hidden sm:inline text-[10px] tabular-nums text-gray-500 flex-shrink-0">
          {aside}
        </span>
      )}
    </button>
  );
}

/* ----------------------------- Table ----------------------------- */

function DamageTable({
  title,
  subtitle,
  badge,
  rows,
}: {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  rows: TableRow[];
}) {
  if (!rows.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <div className="flex items-baseline gap-2 mb-1">
          <h3 className="text-sm font-semibold">{title}</h3>
          {badge}
        </div>
        <p className="text-xs text-gray-500">No damage recorded.</p>
      </section>
    );
  }

  const top = rows[0]?.total ?? 0;
  const rankPad = String(rows.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <header className="flex items-baseline justify-between gap-2 px-4 pt-3 pb-2">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          {badge}
        </div>
        {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th className="pb-2 pr-3 font-medium text-right">Damage</th>
              <th className="pb-2 pr-3 font-medium text-right">DPS</th>
              <th className="pb-2 pr-4 font-medium w-1/3">% of best</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = top ? (r.total / top) * 100 : 0;
              const cls = classFor(r.type);
              const padded = String(i + 1).padStart(rankPad, "0");
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/5 transition-colors"
                >
                  <td className="py-1.5 pl-4 pr-3 text-gray-500 tabular-nums">
                    {padded}
                  </td>
                  <td className="py-1.5 pr-3 max-w-[16rem]">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium">
                    {fmt(r.total)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">
                    {fmt(Math.round(r.perSec))}
                  </td>
                  <td className="py-1.5 pr-4">
                    <ProgressBar pct={pct} color={cls.color} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function KillBadge({ kill }: { kill: boolean }) {
  return kill ? (
    <span className="rounded bg-emerald-500/15 text-emerald-300 text-[10px] px-1.5 py-0.5">
      KILL
    </span>
  ) : (
    <span className="rounded bg-red-500/15 text-red-300 text-[10px] px-1.5 py-0.5">
      WIPE
    </span>
  );
}

function formatDuration(ms: number | undefined): string {
  const total = Math.floor((ms ?? 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}
